import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Stack, TextField, Button, Alert, CircularProgress,
    LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Link,
} from '@mui/material';
import dayjs from 'dayjs';
import AppVersionService from '../services/appVersion.service';
import { Panel, PanelHead } from './dashboard/Surface';
import { DASH } from './dashboard/dashboardTokens';

/**
 * Povinná aktualizácia mobilnej appky.
 *
 * Kto má nižšiu verziu než hranica, uvidí v appke celoobrazovkové okno
 * „Aktualizujte aplikáciu" s tlačidlom na odkaz nižšie. Kontrolu majú až
 * natívne buildy od 1.1.0 — staršie o hranici nevedia, takže ich nezamkne
 * ani prísna hranica. Prázdna verzia = kontrola vypnutá.
 */

// Od tejto natívnej verzie appka hranicu kontroluje.
const GATED_FROM = '1.1.0';
const VERSION_RE = /^\d+\.\d+\.\d+$/;
const PLATFORM_LABEL = { ios: 'iOS', android: 'Android' };
const STORE_LABEL = { ios: 'App Store', android: 'Google Play' };

const compareVersions = (a, b) => {
    const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
    const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i += 1) {
        if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) < (pb[i] || 0) ? -1 : 1;
    }
    return 0;
};

/** Verzia, ktorú hranica naozaj zamkne: pod hranicou A s kontrolou (od 1.1.0). */
const isLockedBy = (version, minVersion) =>
    !!minVersion
    && VERSION_RE.test(String(version))
    && compareVersions(version, GATED_FROM) >= 0
    && compareVersions(version, minVersion) < 0;

const peopleLabel = (n) => (n === 1 ? '1 človek' : n >= 2 && n <= 4 ? `${n} ľudia` : `${n} ľudí`);

const PlatformPanel = ({ policy, usageDays, onSaved }) => {
    const [minVersion, setMinVersion] = useState(policy.minVersion || '');
    const [storeUrl, setStoreUrl] = useState(policy.storeUrl || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        setMinVersion(policy.minVersion || '');
        setStoreUrl(policy.storeUrl || '');
    }, [policy.minVersion, policy.storeUrl]);

    const trimmedVersion = minVersion.trim();
    const versionInvalid = trimmedVersion !== '' && !VERSION_RE.test(trimmedVersion);
    const urlInvalid = !/^https:\/\/\S+$/.test(storeUrl.trim());
    const dirty = trimmedVersion !== (policy.minVersion || '') || storeUrl.trim() !== policy.storeUrl;

    const effectiveMin = versionInvalid ? null : (trimmedVersion || null);
    const usage = policy.usage || [];
    const maxPeople = Math.max(1, ...usage.map((u) => u.people));
    const locked = usage.filter((u) => isLockedBy(u.version, effectiveMin));
    const lockedPeople = locked.reduce((sum, u) => sum + u.people, 0);

    const impact = !effectiveMin
        ? { tone: 'info', text: 'Kontrola je vypnutá. Appka nikoho nezamyká.' }
        : lockedPeople > 0
            ? {
                tone: 'error',
                text: `Zamkne ${peopleLabel(lockedPeople)} na verzii ${locked.map((u) => u.version).join(', ')}.`,
            }
            : {
                tone: 'info',
                text: `Nezamkne nikoho z posledných ${usageDays} dní. Verzie pod ${GATED_FROM} kontrolu nemajú.`,
            };

    const save = async () => {
        setConfirmOpen(false);
        setSaving(true);
        setError(null);
        try {
            await AppVersionService.savePolicy(policy.platform, trimmedVersion || null, storeUrl.trim());
            setSavedAt(new Date());
            onSaved();
        } catch (err) {
            setError(err?.response?.data?.message || 'Uloženie zlyhalo. Skúste to znova.');
        } finally {
            setSaving(false);
        }
    };

    const requestSave = () => (lockedPeople > 0 ? setConfirmOpen(true) : save());

    return (
        <Panel sx={{ flex: '1 1 380px', minWidth: 0 }}>
            <PanelHead
                title={PLATFORM_LABEL[policy.platform]}
                meta={policy.updatedAt ? `Zmenené ${dayjs(policy.updatedAt).format('D. M. YYYY HH:mm')}` : null}
            />
            <Stack gap={2.5}>
                <TextField
                    id={`min-version-${policy.platform}`}
                    label="Minimálna verzia"
                    placeholder="napr. 1.1.0 · prázdne = vypnuté"
                    value={minVersion}
                    onChange={(e) => setMinVersion(e.target.value)}
                    error={versionInvalid}
                    helperText={versionInvalid ? 'Tvar x.y.z, napríklad 1.1.0' : 'Kto má nižšiu, musí aktualizovať.'}
                    size="small"
                    inputProps={{ inputMode: 'decimal', style: DASH.numeric }}
                />
                <Stack gap={0.75}>
                    <TextField
                        id={`store-url-${policy.platform}`}
                        label="Odkaz na tlačidle"
                        value={storeUrl}
                        onChange={(e) => setStoreUrl(e.target.value)}
                        error={urlInvalid}
                        helperText={urlInvalid ? 'Celá adresa začínajúca https://' : `Otvorí sa po kliknutí na „Otvoriť ${STORE_LABEL[policy.platform]}“.`}
                        size="small"
                    />
                    {!urlInvalid ? (
                        <Link href={storeUrl.trim()} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ alignSelf: 'flex-start' }}>
                            Vyskúšať odkaz
                        </Link>
                    ) : null}
                </Stack>

                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Kto na čom je · posledných {usageDays} dní
                    </Typography>
                    <Stack gap={1} sx={{ mt: 1 }}>
                        {usage.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Žiadne údaje.</Typography>
                        ) : usage.map((u) => {
                            const isLocked = isLockedBy(u.version, effectiveMin);
                            return (
                                <Stack key={u.version} direction="row" alignItems="center" gap={1.5}>
                                    <Typography variant="body2" sx={{ ...DASH.numeric, width: 52, color: isLocked ? 'error.main' : 'text.primary' }}>
                                        {u.version}
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(u.people / maxPeople) * 100}
                                        color={isLocked ? 'error' : 'success'}
                                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                    />
                                    <Typography variant="body2" sx={{ ...DASH.numeric, width: 72, textAlign: 'right', color: isLocked ? 'error.main' : 'text.secondary' }}>
                                        {peopleLabel(u.people)}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Kto sa počas obdobia aktualizoval, je započítaný pri oboch verziách.
                    </Typography>
                </Box>

                <Alert severity={impact.tone}>{impact.text}</Alert>
                {error ? <Alert severity="error">{error}</Alert> : null}

                <Stack direction="row" alignItems="center" gap={2}>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={requestSave}
                        disabled={!dirty || saving || versionInvalid || urlInvalid}
                    >
                        {saving ? 'Ukladám…' : `Uložiť ${PLATFORM_LABEL[policy.platform]}`}
                    </Button>
                    {savedAt && !dirty ? (
                        <Typography variant="body2" color="success.main">Uložené</Typography>
                    ) : null}
                </Stack>
            </Stack>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Zamknúť {peopleLabel(lockedPeople)}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Na {PLATFORM_LABEL[policy.platform]} uvidia ľudia s verziou {locked.map((u) => u.version).join(', ')} celoobrazovkové
                        okno a appku nepoužijú, kým si nestiahnu verziu {trimmedVersion}. Je už v obchode schválená?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Zrušiť</Button>
                    <Button color="error" variant="contained" disableElevation onClick={save}>Áno, uložiť</Button>
                </DialogActions>
            </Dialog>
        </Panel>
    );
};

const AppVersions = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await AppVersionService.getPolicies();
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || 'Nepodarilo sa načítať nastavenie verzií.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const policies = useMemo(() => data?.policies || [], [data]);

    if (!data && !error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: DASH.gap }}>
            <Box>
                <Typography sx={{ fontFamily: DASH.display, fontSize: 26 }}>
                    Verzie aplikácie
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                    Kto má nižšiu verziu než minimálnu, uvidí v appke celoobrazovkové okno „Aktualizujte aplikáciu"
                    a appku nepoužije, kým neaktualizuje. Platí od verzie {GATED_FROM}, staršie verzie to nekontrolujú.
                    Pri výpadku servera appka nikoho nezamkne.
                </Typography>
            </Box>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Stack direction="row" flexWrap="wrap" gap={DASH.gap} alignItems="flex-start">
                {policies.map((policy) => (
                    <PlatformPanel key={policy.platform} policy={policy} usageDays={data.usageDays} onSaved={load} />
                ))}
            </Stack>
        </Box>
    );
};

export default AppVersions;
